import type { IceServersResponse } from '../types';

/**
 * ICE servers of a call (`GET /api/calls/ice-servers`): STUN, and the portal's TURN server with credentials that expire.
 * The manager asks before every call and again when the credentials near their end, never caches them past the page.
 */

/** Browser-local diagnostic switch of administrators: use the relay only (the messenger settings). */
export const RELAY_ONLY_STORAGE_KEY = 'orbita.calls.relayOnly';

export function getRelayOnlyOverride(): boolean {
  try {
    return globalThis.localStorage.getItem(RELAY_ONLY_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function setRelayOnlyOverride(on: boolean): void {
  try {
    if (on) globalThis.localStorage.setItem(RELAY_ONLY_STORAGE_KEY, '1');
    else globalThis.localStorage.removeItem(RELAY_ONLY_STORAGE_KEY);
  } catch {
    // Storage may be blocked (private window): the switch then lasts for the page only — nothing to do.
  }
}

export interface IceConfig {
  iceServers: RTCIceServer[];
  /** `relay` when the deployment or an administrator's diagnostic switch says so (and a relay exists), else `all`. */
  iceTransportPolicy: RTCIceTransportPolicy;
  /** When the TURN credentials stop working (epoch ms); null: they do not expire. */
  expiresAt: number | null;
  /** How long the credentials were made to work (ms); 0 when they do not expire. */
  ttlMs: number;
}

export interface IceOptions {
  /** The browser-local diagnostic switch counts for administrators only. */
  isAdmin: boolean;
  relayOnlyOverride: boolean;
}

/** The browser's configuration from the server's answer. Relay-only without a relay would make every call fail: it is not honored then. */
export function toIceConfig(response: IceServersResponse, options: IceOptions): IceConfig {
  const relay = (response.relayOnly || (options.isAdmin && options.relayOnlyOverride)) && response.turnAvailable;
  return {
    iceServers: response.iceServers.map((server) => ({
      urls: server.urls,
      ...(server.username ? { username: server.username } : {}),
      ...(server.credential ? { credential: server.credential } : {}),
    })),
    iceTransportPolicy: relay ? 'relay' : 'all',
    expiresAt: response.expiresAt ? Date.parse(response.expiresAt) : null,
    ttlMs: response.ttlSeconds * 1000,
  };
}

/** Browsers without any ICE server still connect on one machine or a LAN (host candidates). */
export const NO_ICE: IceConfig = { iceServers: [], iceTransportPolicy: 'all', expiresAt: null, ttlMs: 0 };

/**
 * Asks the server for ICE servers and keeps the answer until its credentials near their end. A failing request keeps what
 * there is (an old answer, or none: host candidates only), so a hiccup of the API never stops a call from starting.
 */
export class IceServerSource {
  private config: IceConfig | null = null;
  private readonly fetchServers: () => Promise<IceServersResponse>;
  private readonly options: () => IceOptions;
  private readonly now: () => number;

  constructor(
    fetchServers: () => Promise<IceServersResponse>,
    options: () => IceOptions,
    now: () => number = () => Date.now(),
  ) {
    this.fetchServers = fetchServers;
    this.options = options;
    this.now = now;
  }

  /** Always asks (the start of a call): credentials are made for this call. */
  async refresh(): Promise<IceConfig> {
    try {
      this.config = toIceConfig(await this.fetchServers(), this.options());
    } catch {
      this.config ??= NO_ICE;
    }
    return this.config;
  }

  /** What there is; asks again only when the credentials have a few minutes left (a new peer in a long call). */
  async current(): Promise<IceConfig> {
    if (!this.config || this.expiresSoon(this.config)) return this.refresh();
    return this.config;
  }

  /** Forgets the answer (sign-out: the credentials were made for that person). */
  forget(): void {
    this.config = null;
  }

  private expiresSoon(config: IceConfig): boolean {
    if (config.expiresAt === null) return false;
    // Five minutes in reserve; for a short lifetime (a configured Calls:Turn:CredentialTtl) a quarter of it.
    const reserve = Math.min(5 * 60_000, config.ttlMs / 4);
    return config.expiresAt - this.now() < reserve;
  }
}
